import {
    BulkDiscountSourceLevel,
    PriceAndPromotionInfo,
    SellingPriceDnrInfo,
    SellingPriceDnrLevel
} from "shared/src/types/PriceAndPromotionInfo";
import { BettyBundleWeight, SellingPriceDnRLevel } from "shared/src/types/EvaluateArticleNew";
import { getComponentByName } from "shared/src/components/LazyWrapper";
import { getIntlFormatingForPhraseProject } from "shared/src/i18n/IntlRootMixin";
import { CompulsoryTrackingTags, TrackingTags } from "shared/src/tracking/Tracking";

const BulkDiscountLabel = getComponentByName("sd_pdp_ui/BulkDiscountLabel");

function getDnrLabels(priceAndPromotionInfo: PriceAndPromotionInfo): string[] {
    // Retrieve compact summary of all DnRs to decide whether to show bulk-discounts-promotion or not
    const dnrLabels: string[] = Object.values(priceAndPromotionInfo?.dnrInfo || {})
        .map((dnr: SellingPriceDnrInfo) => dnr.labelId)
        .filter((labelId) => labelId !== null && labelId !== undefined);

    return dnrLabels;
}

interface SortingCriteria {
    sortFn: (item: any) => number;
    ordering: "asc" | "desc";
}

const sortingCriteriaBulkDiscountLevels: SortingCriteria = {
    sortFn: (list) => Number(list?.amountStart ?? "0.00"),
    ordering: "asc"
};

function getDerivedPricePromotionInfo(priceAndPromotionInfo: PriceAndPromotionInfo): Array<BulkDiscountSourceLevel> {
    const summaryDnrInfoLevels: { [key: string]: SellingPriceDnrLevel } | Record<string, SellingPriceDnRLevel> =
        (priceAndPromotionInfo?.summaryDnrInfo || {})?.levels || {};
    const derivedDnrLevels: Array<BulkDiscountSourceLevel> = Object.keys(summaryDnrInfoLevels).map((key) => {
        const dnrLevel = summaryDnrInfoLevels[key];
        return {
            amountStart: key,
            discountPercent: dnrLevel.value ?? "", // This is the 'saved'-percentage
            finalSingleNetPrice: dnrLevel.finalSingleNetPrice,
            finalSingleGrossPrice: dnrLevel.finalSingleGrossPrice
        };
    });

    const sortedDerivedDnrLevels = [...derivedDnrLevels].sort((a, b) => {
        const sortFn = sortingCriteriaBulkDiscountLevels.sortFn;
        const ordering = sortingCriteriaBulkDiscountLevels.ordering;

        const valueA = sortFn(a);
        const valueB = sortFn(b);

        return ordering === "asc" ? valueA - valueB : valueB - valueA;
    });
    return sortedDerivedDnrLevels;
}

type Props = {
    priceAndPromotionInfo: PriceAndPromotionInfo | null;
    weightPerPiece: BettyBundleWeight | undefined | null;
    amountInBasket: number | null | undefined;
    isWeightArticle: boolean;
    useNetPrice: boolean;
    bettyBundleId: string | undefined;
    compulsoryTrackingTags: CompulsoryTrackingTags;
    optionalTrackingTags?: Partial<TrackingTags>;
};

export default function DnRDisplay({
    priceAndPromotionInfo,
    weightPerPiece,
    amountInBasket,
    isWeightArticle,
    useNetPrice,
    bettyBundleId,
    compulsoryTrackingTags,
    optionalTrackingTags
}: Props) {
    if (!priceAndPromotionInfo || !priceAndPromotionInfo.summaryDnrInfo) return null;

    const dnrLabels: Array<string> = getDnrLabels(priceAndPromotionInfo);
    const derivedDnrLevels: Array<BulkDiscountSourceLevel> = getDerivedPricePromotionInfo(priceAndPromotionInfo);
    const intlFormatting = getIntlFormatingForPhraseProject("EXPLORE");

    return BulkDiscountLabel({
        labelText: intlFormatting.formatMessage({ id: "SD_DNR_LABEL_DROPDOWN_TEXT" }),
        summaryDnrLevels: derivedDnrLevels,
        currency: priceAndPromotionInfo?.currency,
        amountInCart: amountInBasket,
        isWeightedArticle: isWeightArticle,
        weightPerPiece: weightPerPiece,
        useNetPrice: useNetPrice,
        dnrLabels: dnrLabels,
        bettyBundleId: bettyBundleId,
        compulsoryTrackingTags: compulsoryTrackingTags,
        optionalTrackingTags: optionalTrackingTags
    });
}
